import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import useStoreState from "@/components/providers/useStoreState";
import { FORM_STATE_DEFAULT, FORM_STATE_ERROR } from "@/constants/FormFieldConstants.mjs";
import AppStore from "@/stores/AppStore";
import { resolveFormFieldStyles } from "@/utils/FormFieldStyleResolver";
import { FORM_WIDGET_TAGS, getFormWidgetProps, whenWidgetRendered } from "./FormField.utils";
import { ensureFormWidgets } from "./loadFormWidgets";

/**
 * Renders a form field on the stage as its `shwt-widget-form-*` web component, styled from its
 * theme style chain. Props go through a ref because arrays can't be JSX attributes; `controlled`
 * tells a real widget to skip the engine configure cycle the stage lacks, and `preview` keeps its
 * controls out of the tab order and makes an errored field invalid so `reportValidity()` shows the
 * browser's message.
 * @param {Object} props
 * @param {import("immutable").Map} props.element - The form-field element.
 * @param {import("immutable").Map} props.theme - The design theme.
 * @param {string} props.layout - `desktop` or `mobile`.
 * @param {boolean} [props.isSelected] - Whether the element is selected on the stage.
 * @param {string} [props.fieldState] - A `FORM_STATE_*` that overrides the stage's state.
 * @param {Object} [props.previewProps] - Widget state the element data doesn't hold (a radio
 *   `value`, `checked`), so previews can show selected styles. Keep it referentially stable.
 */
const FormField = ({
  element,
  theme,
  layout,
  isSelected = false,
  fieldState: fieldStateOverride,
  previewProps,
}) => {
  const widgetRef = useRef(null);
  const appliedStyleRef = useRef({ widget: null, style: {} });
  const type = element.get("type");
  const content = element.get("content");
  const Widget = FORM_WIDGET_TAGS[type];

  // Memoised so `options` keeps its identity and Lit doesn't re-render the widget for nothing.
  const widgetProps = useMemo(() => getFormWidgetProps(content), [content]);
  const storeFieldState = useStoreState(AppStore, AppStore.getFormFieldState);
  const stageFieldState =
    isSelected || storeFieldState === FORM_STATE_ERROR ? storeFieldState : FORM_STATE_DEFAULT;
  const fieldState = fieldStateOverride ?? stageFieldState;
  const { style, attributes } = useMemo(
    () => resolveFormFieldStyles({ element, theme, layout, state: fieldState }),
    [element, theme, layout, fieldState],
  );
  const errored = fieldState === FORM_STATE_ERROR;

  useLayoutEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return undefined;

    // Props set before the widget upgrades are picked up by Lit once its definition arrives.
    ensureFormWidgets(widget.ownerDocument);
    Object.assign(widget, widgetProps, previewProps, attributes, { errored });
  }, [widgetProps, previewProps, attributes, errored]);

  useLayoutEffect(() => {
    const widget = widgetRef.current;
    if (!widget || !errored || !isSelected) return undefined;

    let cancelled = false;
    whenWidgetRendered(widget).then(() => {
      if (!cancelled) widget.reportValidity();
    });
    return () => {
      cancelled = true;
    };
  }, [errored, isSelected]);

  // Typing can't clear the Error preview; capture catches keys that don't bubble.
  // biome-ignore lint/correctness/useExhaustiveDependencies: a new Widget tag is a new host to listen on
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget || !errored) return undefined;

    const blockTyping = (event) => {
      if (event.key !== "Tab") event.preventDefault();
    };
    widget.addEventListener("keydown", blockTyping, true);
    return () => widget.removeEventListener("keydown", blockTyping, true);
  }, [errored, Widget]);

  // Only vars whose value changed are written; each write restyles the widget's shadow tree. The
  // cache is per host, so a new tag (the element's type changed) gets every var from scratch.
  useLayoutEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;

    const applied = appliedStyleRef.current.widget === widget ? appliedStyleRef.current.style : {};
    for (const varName of Object.keys(applied)) {
      if (!(varName in style)) widget.style.removeProperty(varName);
    }
    for (const [varName, value] of Object.entries(style)) {
      if (applied[varName] !== value) widget.style.setProperty(varName, value);
    }
    appliedStyleRef.current = { widget, style };
  }, [style]);

  if (!Widget) {
    return null;
  }

  return <Widget ref={widgetRef} controlled="" preview="" data-testid={`form-field-${type}`} />;
};

export default FormField;
